<template>
  <DataTable
    v-bind="$attrs"
    :columns="columns"
    :rows="rows as Record<string, unknown>[]"
    :visible-columns="effectiveVisibleColumns"
    @column-visibility-changed="handleColumnVisibilityChanged"
    @sort-changed="handleSortChanged"
  >
    <template
      v-for="(_, slot) in $slots"
      #[slot]="slotData"
    >
      <slot
        :name="slot"
        v-bind="slotData as { row: T; value: unknown; column: TableColumn }"
      />
    </template>
  </DataTable>
</template>

<script setup lang="ts" generic="T extends object">
import type { TableColumn } from '@plt/ui-vue';
import { useRouter } from 'vue-router';

import { useTablePreferencesStore } from '@/stores/tablePreferences';

defineOptions({
  inheritAttrs: false,
});

interface Props {
  columns: TableColumn[];
  rows: T[];

  /**
   * Key for storing table preferences in local storage
   * When provided, column visibility and other preferences are persisted
   */
  storageKey?: string;

  /**
   * Initial visible columns configuration
   * Used as fallback when no stored preferences exist
   */
  visibleColumns?: string[];

  /**
   * Whether to automatically sync URL parameters with table state
   * @default true
   */
  syncWithUrl?: boolean;
}

const { columns, rows, storageKey, visibleColumns: initialVisibleColumns, syncWithUrl = true } = defineProps<Props>();

// DataTable types every row as Record<string, unknown>; re-declaring the slots is what gives callers a typed `row`.
defineSlots<{
  [name: string]: (props: { row: T; value: unknown; column: TableColumn }) => unknown;
}>();

const router = useRouter();
const tablePrefsStore = useTablePreferencesStore();

const effectiveVisibleColumns = computed(() => {
  if (storageKey) {
    return tablePrefsStore.getVisibleColumns(storageKey) || initialVisibleColumns;
  }
  return initialVisibleColumns;
});

const handleSortChanged = ({ sortBy, sortOrder }: { sortBy: string; sortOrder: string }) => {
  if (!syncWithUrl) return;

  try {
    const currentQuery = { ...router.currentRoute.value.query };
    router.push({
      query: {
        ...currentQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
      },
    });
  } catch (error) {
    console.error('Failed to update URL with sort parameters:', error);
  }
};

/**
 * Events emitted by AppDataTable
 */
interface Emits {
  /**
   * Emitted when column visibility changes
   */
  'column-visibility-changed': [{ columnKey: string; visible: boolean; visibleColumns: string[] }];
}

const emit = defineEmits<Emits>();

const handleColumnVisibilityChanged = ({
  columnKey,
  visible,
  visibleColumns,
}: {
  columnKey: string;
  visible: boolean;
  visibleColumns: string[];
}) => {
  if (storageKey) {
    try {
      tablePrefsStore.setVisibleColumns(storageKey, visibleColumns);
    } catch (error) {
      console.error('Failed to save column visibility preferences:', error);
    }
  }

  emit('column-visibility-changed', { columnKey, visible, visibleColumns });
};
</script>
